import { useEffect } from "react";
import { useMatches } from "@tanstack/react-router";

export function DocumentMeta() {
  const matches = useMatches();

  useEffect(() => {
    // Collect all meta tags from matches
    const metaTags: Array<{ [key: string]: string }> = [];
    const linkTags: Array<{ [key: string]: string }> = [];
    const schemaTags: Array<any> = [];
    let title = "LetsListBusiness — Discover Local Businesses & Services"; // Default title

    matches.forEach((match) => {
      // Access the head function if it exists, or meta directly
      // In @tanstack/react-router, loaderData is available on match
      let headResult: any = {};
      
      // Some routes might export meta directly, some might use a head() function
      if (typeof (match as any).routeContext?.head === "function") {
        headResult = (match as any).routeContext.head({
          loaderData: match.loaderData,
          params: match.params,
        });
      } else if (typeof match.context?.head === "function") {
         headResult = match.context.head({
          loaderData: match.loaderData,
          params: match.params,
        });
      }
      
      // Handle the Route options directly (static meta)
      const routeOptions = (match as any).routeContext || (match as any).routeOptions || (match as any).options;
      if (routeOptions) {
        if (typeof routeOptions.head === 'function') {
           const res = routeOptions.head({ loaderData: match.loaderData, params: match.params });
           if (res.meta) metaTags.push(...res.meta);
           if (res.links) linkTags.push(...res.links);
           if (res.schema) {
             if (Array.isArray(res.schema)) {
               schemaTags.push(...res.schema);
             } else {
               schemaTags.push(res.schema);
             }
           }
           if (res.scripts) {
             res.scripts.forEach((script: any) => {
               if (script.type === "application/ld+json" && script.children) {
                 try {
                   const parsed = JSON.parse(script.children);
                   schemaTags.push(parsed);
                 } catch (e) {
                   console.error("Failed to parse schema script children", e);
                 }
               }
             });
           }
        } else if (routeOptions.meta) {
           const meta = typeof routeOptions.meta === 'function' ? routeOptions.meta({ loaderData: match.loaderData, params: match.params }) : routeOptions.meta;
           if (Array.isArray(meta)) metaTags.push(...meta);
        }
      }
    });

    // Extract title from metaTags
    const titleTag = metaTags.find((tag) => tag.title);
    if (titleTag) {
      title = titleTag.title;
    }

    // Update document title
    document.title = title;

    // Helper to update or create DOM tags
    const updateTags = (
      selector: string,
      tagName: string,
      attributes: { [key: string]: string }
    ) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(tagName);
        document.head.appendChild(el);
      }
      Object.entries(attributes).forEach(([key, value]) => {
        el!.setAttribute(key, value);
      });
      el!.setAttribute("data-router-injected", "true");
    };

    // Clean up previously injected tags
    document.querySelectorAll('[data-router-injected="true"]').forEach((el) => {
      el.remove();
    });

    // Inject meta tags
    metaTags.forEach((tag) => {
      if (tag.title) return; // Handled above
      if (tag.name) {
        updateTags(`meta[name="${tag.name}"]`, "meta", tag);
      } else if (tag.property) {
        updateTags(`meta[property="${tag.property}"]`, "meta", tag);
      }
    });

    // Inject link tags (like canonical)
    linkTags.forEach((tag) => {
      if (tag.rel) {
        updateTags(`link[rel="${tag.rel}"]`, "link", tag);
      }
    });

    // Inject schema tags
    schemaTags.forEach((schema, index) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(schema);
      script.setAttribute("data-router-injected", "true");
      script.setAttribute("id", `schema-${index}`);
      document.head.appendChild(script);
    });
    
  }, [matches]);

  return null;
}
